import React, { useState, useContext, useEffect } from 'react';
import { ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { doc, updateDoc, arrayUnion, getDoc, setDoc, addDoc, collection } from 'firebase/firestore';
import { db, storage } from '../../services/firebase';
import { UserContext } from '../../contexts/UserContext';
import './UploadPortal.css';

const UploadPortal = ({ onClose }) => {
  const { user, firebaseUser, isLoading } = useContext(UserContext);
  
  // File handling state
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [compressionProgress, setCompressionProgress] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);

  // Enhanced metadata state
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [taggedUsers, setTaggedUsers] = useState('');
  const [projectType, setProjectType] = useState('personal');
  const [location, setLocation] = useState('');
  const [isProcessingMetadata, setIsProcessingMetadata] = useState(false);

  // Project type options
  const projectTypes = [
    { value: 'personal', label: 'Personal Work' },
    { value: 'client', label: 'Client Project' },
    { value: 'collaboration', label: 'Collaboration' },
    { value: 'study', label: 'Study/Experiment' },
    { value: 'commission', label: 'Commission' }
  ];

  // Debug only once when component mounts
  useEffect(() => {
    if (!isLoading) {
      console.log('🔍 UploadPortal mounted - User can upload:', 
        user && user.email && !user.isGuest && user.approvedForUploads);
    }
  }, [isLoading, user]);

  // Check if user can upload
  const canUpload = user && user.email && !user.isGuest && user.approvedForUploads;

  const getUsername = () => {
    if (user?.email) {
      return user.email.split('@')[0];
    }
    const path = window.location.pathname;
    const pathParts = path.split('/');
    const profileIndex = pathParts.indexOf('profiles');
    if (profileIndex !== -1 && pathParts.length > profileIndex + 1) {
      return pathParts[profileIndex + 1];
    }
    return 'testuser123';
  };

  const compressImage = (file, maxWidth = 1920, quality = 0.85) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            const compressedFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality
        );
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const processFile = async (selectedFile) => {
    if (!selectedFile.type.startsWith('image/')) {
      setRejectedFiles(prev => [...prev, selectedFile.name]);
      setUploadStatus('❌ Only images are allowed');
      return null;
    }

    const maxSize = 20 * 1024 * 1024; // 20MB
    if (selectedFile.size > maxSize) {
      setRejectedFiles(prev => [...prev, selectedFile.name]);
      setUploadStatus('❌ Image too large. Maximum size is 20MB');
      return null;
    }

    setCompressionProgress('Processing image...');

    try {
      setCompressionProgress('Compressing image...');
      const processedFile = await compressImage(selectedFile);
      
      const originalSize = (selectedFile.size / 1024 / 1024).toFixed(2);
      const compressedSize = (processedFile.size / 1024 / 1024).toFixed(2);
      setCompressionProgress(`Compressed: ${originalSize}MB → ${compressedSize}MB`);

      return processedFile;
    } catch (error) {
      setCompressionProgress('');
      setUploadStatus(`❌ File processing error: ${error.message}`);
      setRejectedFiles(prev => [...prev, selectedFile.name]);
      return null;
    }
  };

  const parseTaggedUsers = (tagString) => {
    // Extract @username mentions from the tagged users field
    const matches = tagString.match(/@[\w.]+/g);
    return matches ? matches.map(tag => tag.substring(1)) : [];
  };

  const processKeywords = (keywordString) => {
    // Clean and process comma-separated keywords
    return keywordString
      .split(',')
      .map(keyword => keyword.trim().toLowerCase())
      .filter(keyword => keyword.length > 0);
  };

  const enhanceMetadataWithAI = async (description, keywords) => {
    // Background AI enhancement (optional - can be disabled to save API costs)
    if (!description && !keywords) return [];

    try {
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const enhanceContentMetadata = httpsCallable(functions, 'enhanceContentMetadata');

      const result = await enhanceContentMetadata({
        description,
        keywords,
        projectType
      });

      return result.data.enhancedKeywords || [];
    } catch (error) {
      console.log('AI enhancement unavailable:', error);
      return [];
    }
  };

  const handleFileChange = async (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setFile(null);
      setPreview(null);
      return;
    }

    const selectedFile = e.target.files[0];
    const processedFile = await processFile(selectedFile);
    
    if (processedFile) {
      setFile(processedFile);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setCompressionProgress('');
      };
      reader.readAsDataURL(processedFile);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (!droppedFile) return;

    const processedFile = await processFile(droppedFile);
    
    if (processedFile) {
      setFile(processedFile);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setCompressionProgress('');
      };
      reader.readAsDataURL(processedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !canUpload) {
      setUploadStatus('❌ Upload not allowed');
      return;
    }

    // Validate required fields
    if (!description.trim()) {
      setUploadStatus('❌ Description is required');
      return;
    }

    setIsUploading(true);
    setUploadStatus('Uploading...');

    try {
      const reader = new FileReader();
      const base64Data = await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Process metadata
      setIsProcessingMetadata(true);
      const userKeywords = processKeywords(keywords);
      const taggedUsersList = parseTaggedUsers(taggedUsers);
      
      // Optional AI enhancement (comment out to save API costs)
      const aiKeywords = await enhanceMetadataWithAI(description, keywords);
      const allKeywords = [...new Set([...userKeywords, ...aiKeywords])];

      // Import Firebase Functions
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const uploadUserImage = httpsCallable(functions, 'uploadUserImage');

      const result = await uploadUserImage({
        imageData: base64Data,
        fileName: file.name,
        userId: user.uid
      });

      if (result.data.success) {
        // Create content post document
        const postData = {
          userId: user.uid,
          username: user.displayName || user.email.split('@')[0],
          imageUrl: result.data.downloadURL,
          description: description.trim(),
          keywords: allKeywords,
          userProvidedKeywords: userKeywords,
          aiEnhancedKeywords: aiKeywords,
          taggedUsers: taggedUsersList,
          projectType,
          location: location.trim() || null,
          createdAt: new Date(),
          updatedAt: new Date(),
          likes: 0,
          views: 0,
          status: 'published'
        };

        // Save to posts collection
        await addDoc(collection(db, 'posts'), postData);

        // Update user profile with photo
        const userRef = doc(db, 'users', user.uid);
        await updateDoc(userRef, {
          photos: arrayUnion(result.data.downloadURL)
        });

        setUploadStatus('✅ Upload successful!');
        setTimeout(() => {
          if (onClose) onClose();
          window.location.reload();
        }, 1500);
      } else {
        throw new Error(result.data.error || 'Upload failed');
      }

    } catch (error) {
      console.error('❌ Upload error:', error);
      setUploadStatus(`❌ ${error.message}`);
    } finally {
      setIsUploading(false);
      setIsProcessingMetadata(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  if (isLoading) {
    return (
      <div className="upload-portal-overlay" onClick={handleClose}>
        <div className="upload-portal-modal" onClick={(e) => e.stopPropagation()}>
          <div className="upload-portal-header">
            <h3>Loading...</h3>
            <button className="close-button" onClick={handleClose}>×</button>
          </div>
          <div className="upload-portal-content">
            <p>Loading user data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user || !user.email || user.isGuest) {
    return (
      <div className="upload-portal-overlay" onClick={handleClose}>
        <div className="upload-portal-modal" onClick={(e) => e.stopPropagation()}>
          <div className="upload-portal-header">
            <h3>Login Required</h3>
            <button className="close-button" onClick={handleClose}>×</button>
          </div>
          <div className="upload-portal-content">
            <p>Please log in to upload images to your profile.</p>
            <div className="upload-portal-actions">
              <button onClick={handleClose} className="cancel-button">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user.approvedForUploads) {
    return (
      <div className="upload-portal-overlay" onClick={handleClose}>
        <div className="upload-portal-modal" onClick={(e) => e.stopPropagation()}>
          <div className="upload-portal-header">
            <h3>Upload Permissions Required</h3>
            <button className="close-button" onClick={handleClose}>×</button>
          </div>
          <div className="upload-portal-content">
            <p>Your account doesn't have upload permissions yet.</p>
            <p>Contact an administrator to request contributor access.</p>
            <div className="upload-portal-actions">
              <button onClick={handleClose} className="cancel-button">
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="upload-portal-overlay" onClick={handleClose}>
      <div className="upload-portal-modal upload-portal-enhanced" onClick={(e) => e.stopPropagation()}>
        <div className="upload-portal-header">
          <h3>Share Your Work</h3>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        <div className="upload-portal-content">
          <div 
            className={`upload-vortex ${isDragOver ? 'drag-over' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.querySelector('.file-input').click()}
          >
            <div className="vortex-animation">
              <span>{isDragOver ? 'DROP IMAGE HERE' : 'DRAG & DROP'}</span>
            </div>
          </div>

          <div className="upload-stage">
            <p>Drag and drop an image into the portal above, or use the file selector below.</p>
            <p style={{ fontSize: '12px', color: '#666' }}>Maximum size: 20MB • Images only</p>
          </div>

          <div className="file-input-container">
            <input
              type="file"
              onChange={handleFileChange}
              accept="image/*"
              disabled={isUploading}
              className="file-input"
            />

            {compressionProgress && (
              <div className="compression-status">
                {compressionProgress}
              </div>
            )}

            {preview && file && (
              <div className="file-preview">
                <img src={preview} alt="Preview" className="preview-image" />
                <div className="file-info">
                  <p><strong>File:</strong> {file.name}</p>
                  <p><strong>Size:</strong> {(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  <p><strong>Type:</strong> {file.type}</p>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Metadata Form */}
          <div className="metadata-form">
            <div className="form-section">
              <label htmlFor="description">Project Description *</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe your project, process, inspiration, or context..."
                rows={4}
                className="description-input"
                disabled={isUploading}
              />
            </div>

            <div className="form-row">
              <div className="form-section">
                <label htmlFor="keywords">Keywords</label>
                <input
                  id="keywords"
                  type="text"
                  value={keywords}
                  onChange={(e) => setKeywords(e.target.value)}
                  placeholder="minimal, concrete, residential, los angeles"
                  className="keywords-input"
                  disabled={isUploading}
                />
                <small>Comma-separated terms for discoverability</small>
              </div>

              <div className="form-section">
                <label htmlFor="projectType">Project Type</label>
                <select
                  id="projectType"
                  value={projectType}
                  onChange={(e) => setProjectType(e.target.value)}
                  className="project-type-select"
                  disabled={isUploading}
                >
                  {projectTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-section">
                <label htmlFor="taggedUsers">Credits & Collaborators</label>
                <input
                  id="taggedUsers"
                  type="text"
                  value={taggedUsers}
                  onChange={(e) => setTaggedUsers(e.target.value)}
                  placeholder="@photographer @client @collaborator"
                  className="tagged-users-input"
                  disabled={isUploading}
                />
                <small>Use @username to credit collaborators</small>
              </div>

              <div className="form-section">
                <label htmlFor="location">Location (Optional)</label>
                <input
                  id="location"
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Los Angeles, CA"
                  className="location-input"
                  disabled={isUploading}
                />
              </div>
            </div>
          </div>

          {(uploadedFiles.length > 0 || rejectedFiles.length > 0) && (
            <div className="upload-records">
              <div>
                <h4>Uploaded:</h4>
                <ul>
                  {uploadedFiles.map((name, i) => <li key={i}>✅ {name}</li>)}
                </ul>
              </div>
              <div>
                <h4>Rejected:</h4>
                <ul>
                  {rejectedFiles.map((name, i) => <li key={i}>❌ {name}</li>)}
                </ul>
              </div>
            </div>
          )}

          <div className="upload-portal-actions">
            <button
              onClick={handleUpload}
              disabled={!file || !description.trim() || isUploading}
              className={`upload-button ${isUploading ? 'uploading' : ''}`}
            >
              {isUploading ? 
                (isProcessingMetadata ? 'Processing metadata...' : 'Uploading...') 
                : 'Share Work'
              }
            </button>
            
            <button onClick={handleClose} className="cancel-button">
              Cancel
            </button>
          </div>

          {uploadStatus && (
            <div className={`status-message ${uploadStatus.includes('❌') ? 'error' : uploadStatus.includes('✅') ? 'success' : ''}`}>
              {uploadStatus}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadPortal;